import React, { useEffect, useState, useCallback } from "react";
import { Clock, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { RequestUpdateSheet } from "./RequestUpdateSheet";

/* ── Severity grammar ─────────────────────────────────────────────────
   Calm operational palette. NO aggressive reds. "blocking" gets a
   muted rose accent; warnings get amber; info gets slate. Borders are
   1px and the surface is white with soft elevation — fits the existing
   Deal Room card grammar exactly.                                       */

const SEVERITY_STYLE = {
  blocking: {
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    accent: "bg-rose-500/60",
    label: "Blocking",
  },
  warning: {
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    accent: "bg-amber-500/60",
    label: "Slowing momentum",
  },
  info: {
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    accent: "bg-slate-400/60",
    label: "For awareness",
  },
};

const ROLE_LABEL = {
  solicitor_seller: "Vendor's solicitor",
  solicitor_buyer:  "Buyer's solicitor",
  buyer:            "Buyer",
  seller:           "Seller",
  broker:           "Mortgage broker",
  agent:            "Estate agent",
};

const fmtAge = (days) => {
  if (!days || days <= 0) return "Today";
  if (days === 1) return "1 day";
  if (days < 14) return `${days} days`;
  if (days < 60) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
};

const fmtChasedAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `Chased ${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Chased ${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `Chased ${days} day${days === 1 ? "" : "s"} ago`;
};

/**
 * BlockerStrip — operational momentum surface.
 *
 * Renders the current transaction blockers at the top of the Document
 * Hub. Each card answers: what is blocking, who owns it, how long it
 * has been outstanding, and what the next operational action is. The
 * "Request Update" CTA opens a lightweight composer sheet that
 * prefills a calm operational message and emits an activity row when
 * sent.
 */
export const BlockerStrip = ({ dealId, onActivity }) => {
  const [blockers, setBlockers] = useState(null);
  const [error, setError] = useState(null);
  const [activeBlocker, setActiveBlocker] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/blockers`);
      setBlockers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      // Endpoint optional — silent on 404 so the strip stays calm.
      setBlockers([]);
      setError(e?.message);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const handleSent = (blockerId) => {
    setActiveBlocker(null);
    toast.success("Update requested");
    load();
    onActivity?.(blockerId);
  };

  if (blockers === null) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="blocker-strip-loading">
        <div className="flex items-center gap-2 text-slate-500 text-[12px]">
          <Loader2 size={13} className="animate-spin" /> Scanning for blockers…
        </div>
      </div>
    );
  }

  if (blockers.length === 0) {
    return (
      <div
        className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 p-4"
        data-testid="blocker-strip-empty"
      >
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <ShieldCheck size={14} />
          </span>
          <div>
            <p className="text-[12.5px] font-bold text-slate-900">No blockers detected</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">
              All operational items look healthy. ChainFlow will surface anything that starts to drift.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="blocker-strip">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">Blockers</p>
          <p className="text-[12.5px] text-slate-900 font-bold mt-0.5">
            {blockers.length} item{blockers.length === 1 ? "" : "s"} currently outstanding
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="blocker-list">
        {blockers.map((b) => (
          <BlockerCard
            key={b.id}
            blocker={b}
            onRequest={() => setActiveBlocker(b)}
          />
        ))}
      </ul>

      {activeBlocker && (
        <RequestUpdateSheet
          dealId={dealId}
          blocker={activeBlocker}
          onClose={() => setActiveBlocker(null)}
          onSent={() => handleSent(activeBlocker.id)}
        />
      )}
    </div>
  );
};

const BlockerCard = ({ blocker, onRequest }) => {
  const style = SEVERITY_STYLE[blocker.severity] || SEVERITY_STYLE.info;
  const chased = fmtChasedAgo(blocker.last_chased_at);
  return (
    <li
      className="rounded-2xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm"
      data-testid={`blocker-card-${blocker.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Left severity accent — 3px high vertical stroke, calm. */}
        <span className={`w-1 self-stretch rounded-full ${style.accent}`} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13px] font-bold text-slate-900 truncate">{blocker.title}</p>
            <span
              className={`text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${style.pill}`}
              data-testid={`blocker-severity-${blocker.id}`}
            >
              {style.label}
            </span>
          </div>
          <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{blocker.description}</p>
          <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock size={11} className="text-slate-400" />
              <span className="font-semibold text-slate-700">{fmtAge(blocker.age_days)}</span> outstanding
            </span>
            <span className="text-slate-300">·</span>
            <span><span className="font-semibold text-slate-700">{ROLE_LABEL[blocker.owner_role] || blocker.owner_role}</span></span>
            {chased && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{chased}</span>
              </>
            )}
          </div>
          <p className="text-[11.5px] text-slate-500 mt-2">
            <span className="font-semibold text-slate-700">Next action:</span> {blocker.next_action}
          </p>
        </div>
        <button
          type="button"
          onClick={onRequest}
          disabled={!blocker.can_request}
          className={
            "shrink-0 h-9 px-3.5 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 transition-all " +
            (blocker.can_request
              ? "bg-slate-900 text-white hover:bg-slate-700 hover:-translate-y-0.5"
              : "bg-slate-100 text-slate-400 cursor-not-allowed")
          }
          title={blocker.can_request ? "Open the operational request" : "No recipient on file for this role"}
          data-testid={`blocker-request-${blocker.id}`}
        >
          Request Update <ChevronRight size={12} />
        </button>
      </div>
    </li>
  );
};

export default BlockerStrip;
